import {
  hasExtractionSource,
  markSourceFileOcrFailed,
} from '../mock-data/extraction-sources.mock';
import {
  findProjectById,
  MOCK_PROJECT_DETAILS,
} from '../mock-data/projects.mock';
import {
  createMockResponse,
  createMockErrorResponse,
} from '../mock.interceptor';

import type { ExtractionStatus } from '../../features/extraction-sources/extraction-sources.models';
import type {
  AiProcessStatus,
  ProjectApiResponse,
  UpdateProjectMetadataApiRequest,
} from '../../features/projects/projects.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * ポーリング動作シミュレーション用のミュータブルなステータス上書きマップ。
 * projectId をキーとし、オーバーライドする各ステータスと呼び出し回数を保持する。
 * handleGetProject でこのマップを参照して aiProcessStatus / extractionStatus をオーバーライドする。
 * アップロード後に setMutableProjectStatus() で processing をセットすることで
 * ポーリング開始をトリガーし、3 回目以降の GET で ready_to_apply を返すシミュレーションを実現する。
 */
export const MUTABLE_PROJECT_STATUS: Record<
  string,
  {
    aiProcessStatus: AiProcessStatus;
    extractionStatus: ExtractionStatus;
    callCount: number;
    latestExtractionRunPublicId: string | null;
  }
> = {};

/**
 * ポーリング動作確認のためにプロジェクトのステータスを上書きセットする。
 * アップロード成功後（handleGetUploadUrl 等）から呼び出す。
 * @param projectId 対象プロジェクトID
 * @param aiProcessStatus セットする AI処理ステータス
 * @param extractionStatus セットする抽出処理ステータス（デフォルト: 'processing'）
 */
export function setMutableProjectStatus(
  projectId: string,
  aiProcessStatus: AiProcessStatus,
  extractionStatus: ExtractionStatus = 'processing',
): void {
  MUTABLE_PROJECT_STATUS[projectId] = {
    aiProcessStatus,
    extractionStatus,
    callCount: 0,
    latestExtractionRunPublicId: null,
  };
}

/**
 * /projects* へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス
 * AI process のパス（/projects/:id/ai-process/...）は ai-process.handler.ts が処理するため除外
 */
export function handleProjectsRequest(
  method: string,
  path: string,
  body: unknown,
): Observable<HttpResponse<unknown>> | null {
  // GET /projects/:id - プロジェクト詳細取得（ai-process を含むパスは除外）
  const getDetailMatch = /^\/projects\/([^/]+)$/.exec(path);
  if (method === 'GET' && getDetailMatch) {
    const projectId = getDetailMatch[1];
    return handleGetProject(projectId);
  }

  // PATCH /projects/:id/metadata - プロジェクトメタデータ更新
  const patchMetadataMatch = /^\/projects\/([^/]+)\/metadata$/.exec(path);
  if (method === 'PATCH' && patchMetadataMatch) {
    const projectId = patchMetadataMatch[1];
    return handleUpdateProjectMetadata(
      projectId,
      body as UpdateProjectMetadataApiRequest,
    );
  }

  return null;
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:id - プロジェクト詳細取得
 * MUTABLE_PROJECT_STATUS にエントリがある場合は aiProcessStatus / extractionStatus をオーバーライドして返す。
 * 呼び出し回数が 3 回目以降は ready_to_apply に自動遷移し、latestExtractionRunPublicId をセット
 * （ポーリング動作の視覚確認用）。ただし固定データが解析失敗のプロジェクトは failed で終端する
 * （失敗バナーの文言確認用。Issue #475 Phase 5.5）。
 */
function handleGetProject(
  projectId: string,
): Observable<HttpResponse<ProjectApiResponse | { message: string }>> {
  const project = findProjectById(projectId);
  if (!project) {
    return createMockErrorResponse('プロジェクトが見つかりません', 404);
  }

  // ミュータブルステータスのオーバーライド処理（ポーリングシミュレーション）
  let overrideAiProcessStatus: AiProcessStatus | undefined;
  let overrideExtractionStatus: ExtractionStatus | undefined;
  let overrideLatestExtractionRunPublicId: string | null | undefined;

  const mutableEntry = MUTABLE_PROJECT_STATUS[projectId] as
    | {
        aiProcessStatus: AiProcessStatus;
        extractionStatus: ExtractionStatus;
        callCount: number;
        latestExtractionRunPublicId: string | null;
      }
    | undefined;

  if (mutableEntry !== undefined) {
    mutableEntry.callCount++;
    // 3 回目以降の GET で completed / ready_to_apply に遷移するシミュレーション。
    // processing のときのみ遷移させる（cancelled / failed 等はそのまま保持する）。
    if (
      mutableEntry.callCount >= 3 &&
      mutableEntry.aiProcessStatus === 'processing'
    ) {
      // extraction も解析中だった場合のみ遷移させる
      // （AI処理単独の実行で extraction を書き換えないため）
      if (
        mutableEntry.extractionStatus === 'processing' &&
        project.extractionStatus === 'failed'
      ) {
        // 固定データが「解析失敗のプロジェクト」なら失敗で終端させる（Issue #475 Phase 5.5）。
        // 失敗バナーの文言（理由ごとの 3 状態）を Mock で目視確認できるようにするため。
        // 理由は固定データ側の extractionFailedReason をそのまま使う。
        mutableEntry.extractionStatus = 'failed';
        // 実 API の ExtractionFailedProject は aiProcessStatus='idle' 固定
        // （domain: project.ts の ExtractionFailedProject）。ここで 'completed' に
        // すると「解析失敗なのに AI 審査は完了」という実 API では作れない組合せを
        // wire に載せてしまうため idle に戻す。
        mutableEntry.aiProcessStatus = 'idle';
        markSourceFileOcrFailed(
          projectId,
          project.extractionFailedReason ?? null,
        );
      } else {
        mutableEntry.aiProcessStatus = 'completed';
        if (mutableEntry.extractionStatus === 'processing') {
          mutableEntry.extractionStatus = 'ready_to_apply';
          mutableEntry.latestExtractionRunPublicId = 'run_mock123';
        }
      }
    }
    overrideAiProcessStatus = mutableEntry.aiProcessStatus;
    overrideExtractionStatus = mutableEntry.extractionStatus;
    overrideLatestExtractionRunPublicId =
      mutableEntry.latestExtractionRunPublicId;
    MUTABLE_PROJECT_STATUS[projectId] = mutableEntry;
  }

  return createMockResponse<ProjectApiResponse>(
    buildProjectResponse(projectId, project, {
      aiProcessStatus: overrideAiProcessStatus,
      extractionStatus: overrideExtractionStatus,
      latestExtractionRunPublicId: overrideLatestExtractionRunPublicId,
    }),
  );
}

/**
 * 固定データ + ステータス上書きから wire に載せる Project 応答を組み立てる。
 *
 * GET / PATCH の両ハンドラから呼ぶ（Issue #475 Phase 5.5）。ProjectsStore は PATCH 応答で
 * Project キャッシュを丸ごと置換するため、PATCH だけ整合ロジックを欠くと
 * 「再アップロード中にメモを編集したら固定データの failed 表示に巻き戻る」といった
 * Mock 固有の挙動になる。
 *
 * 実 API では作れない組合せを載せないためのゲート:
 *   - `extractionFailedReason` は `extractionStatus === 'failed'` のときだけ載せる
 *   - `hasExtractionSource` は MOCK_SOURCE_FILES から動的算出する
 */
function buildProjectResponse(
  projectId: string,
  project: ProjectApiResponse,
  override: {
    aiProcessStatus?: AiProcessStatus;
    extractionStatus?: ExtractionStatus;
    latestExtractionRunPublicId?: string | null;
  } = {},
): ProjectApiResponse {
  const effectiveExtractionStatus =
    override.extractionStatus ?? project.extractionStatus;
  return {
    ...project,
    hasExtractionSource: hasExtractionSource(projectId),
    ...(override.aiProcessStatus !== undefined && {
      aiProcessStatus: override.aiProcessStatus,
    }),
    ...(override.extractionStatus !== undefined && {
      extractionStatus: override.extractionStatus,
    }),
    ...(override.latestExtractionRunPublicId !== undefined && {
      latestExtractionRunPublicId: override.latestExtractionRunPublicId,
    }),
    extractionFailedReason:
      effectiveExtractionStatus === 'failed'
        ? (project.extractionFailedReason ?? null)
        : null,
  };
}

/**
 * PATCH /api/v0/tenants/:tenantId/projects/:id/metadata - プロジェクトメタデータ更新
 *
 * 応答は GET と同じ整合ロジック（buildProjectResponse）を通す。実 API は現在値を返し、
 * viewer は PATCH 応答でキャッシュを丸ごと置換するため。
 */
function handleUpdateProjectMetadata(
  projectId: string,
  body: UpdateProjectMetadataApiRequest,
): Observable<HttpResponse<ProjectApiResponse | { message: string }>> {
  const project = findProjectById(projectId);
  if (!project) {
    return createMockErrorResponse('プロジェクトが見つかりません', 404);
  }

  // メタデータを更新（mark, memo）
  const updatedProject: ProjectApiResponse = {
    ...project,
    ...(body.mark !== undefined && { mark: body.mark }),
    ...(body.memo !== undefined && { memo: body.memo }),
    updatedAt: new Date().toISOString(),
  };

  // メモリ上のMockデータも更新（同一セッション内で変更を保持）
  MOCK_PROJECT_DETAILS[projectId] = updatedProject;

  // ポーリングシミュレーション中の上書きは PATCH でも維持する
  // （callCount は進めない。PATCH はポーリングではない）
  // Record の index 型は undefined を含まないため handleGetProject と同じく明示 cast する
  const mutableEntry = MUTABLE_PROJECT_STATUS[projectId] as
    | {
        aiProcessStatus: AiProcessStatus;
        extractionStatus: ExtractionStatus;
        latestExtractionRunPublicId: string | null;
      }
    | undefined;
  return createMockResponse<ProjectApiResponse>(
    buildProjectResponse(
      projectId,
      updatedProject,
      mutableEntry === undefined
        ? {}
        : {
            aiProcessStatus: mutableEntry.aiProcessStatus,
            extractionStatus: mutableEntry.extractionStatus,
            latestExtractionRunPublicId:
              mutableEntry.latestExtractionRunPublicId,
          },
    ),
  );
}
